// ============================================================
// Colonie.js  —  src/components/Colonie.js
// Main Colonie module — imports all modal components
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  Sun, RefreshCw, Search, DollarSign, Link,
  Settings, Check, Loader, ExternalLink,
  BarChart2, FileText, RotateCcw, Plus,
} from 'lucide-react';

import { P, PKG_SHORT, printColonieReceipt } from './modals/ColoniePrintUtils';
import ColoniePaymentModal     from './modals/ColoniePaymentModal';
import ColonieAddAddonModal    from './modals/ColonieAddAddonModal';
import ColonieLinkFamilyModal  from './modals/ColonieLinkFamilyModal';
import ColonieDailyReportModal from './modals/ColonieDailyReportModal';
import ColonieAdminReport      from './modals/ColonieAdminReport';
import ColonieSettingsModal    from './modals/ColonieSettingsModal';
import ColonieConvertModal    from './modals/ColonieConvertModal';

const BADGE = {
  New:'bg-gray-100 text-gray-600 border-gray-300',
  Enrolled:'bg-blue-100 text-blue-700 border-blue-300',
  PartialPaid:'bg-orange-100 text-orange-700 border-orange-300',
  Paid:'bg-green-100 text-green-700 border-green-300',
  Confirmed:'bg-purple-100 text-purple-700 border-purple-300',
  Cancelled:'bg-red-100 text-red-700 border-red-300',
  Pending:'bg-yellow-100 text-yellow-700 border-yellow-300',
  Unpaid:'bg-red-100 text-red-700 border-red-300',
  Partial:'bg-orange-100 text-orange-700 border-orange-300',
};
const Badge = ({ status }) => (
  <span className={`px-2 py-0.5 text-xs font-semibold rounded-full border ${BADGE[status]||'bg-gray-100 text-gray-600 border-gray-300'}`}>
    {status}
  </span>
);

const Colonie = ({ apiCall, hasRole, showSuccess, showError }) => {
  const [regs, setRegs]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [syncing, setSyncing]   = useState(false);
  const [confirming, setConf]   = useState(null);
  const [renewing, setRenewing] = useState(null);
  const [search, setSearch]     = useState('');
  const [filter, setFilter]     = useState('All');

  const [payModal, setPay]          = useState(null);
  const [addonModal, setAddon]      = useState(null);
  const [linkModal, setLink]        = useState(null);
  const [showDaily, setDaily]       = useState(false);
  const [showAdmin, setAdmin]       = useState(false);
  const [showSettings, setSettings] = useState(false);
  const [convertModal, setConvert]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filter !== 'All') p.set('status', filter);
      if (search) p.set('search', search);
      setRegs(await apiCall(`/colonie/registrations?${p}`));
    } catch (e) { showError(e.message); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiCall, filter, search]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await apiCall('/colonie/sync', { method: 'POST' });
      showSuccess(r.message || 'Sync complete');
      load();
    } catch (e) { showError('Sync failed: ' + e.message); }
    finally { setSyncing(false); }
  };

  const handleRenewMonthly = async (reg) => {
    if (!window.confirm(`Add month 2 renewal ($${P.monthly3Days}) for ${reg.ChildName}?`)) return;
    setRenewing(reg.RegistrationID);
    try {
      await apiCall(`/colonie/registrations/${reg.RegistrationID}/renew-monthly`, { method: 'POST' });
      showSuccess(`Month 2 renewal added for ${reg.ChildName}`);
      load();
    } catch (e) { showError(e.message); }
    finally { setRenewing(null); }
  };

  const handleConfirmExternal = async (reg) => {
    if (!window.confirm(`Confirm ${reg.ChildName} on the Colonie portal?`)) return;
    setConf(reg.RegistrationID);
    try {
      const r = await apiCall(`/colonie/confirm-external/${reg.RegistrationID}`, { method: 'POST' });
      if (r.success) showSuccess(`✓ ${reg.ChildName} confirmed on portal`);
      else showError(r.message || 'Auto-confirm failed. Please confirm manually.');
      load();
    } catch (e) { showError(e.message); }
    finally { setConf(null); }
  };


  const handleConvertSaved = (result, reg) => {
    setConvert(null);
    showSuccess(`${reg.ChildName} converted to ${result.registration?.PackageType}`);
    // Fire portal confirm after conversion
    const regId = reg.RegistrationID;
    apiCall(`/colonie/confirm-external/${regId}`, { method: 'POST' })
      .then(r => {
        if (r.success) showSuccess(`✓ ${reg.ChildName} confirmed on Colonie portal`);
        else showError(`Converted, but portal confirm failed: ${r.message}`);
        load();
      })
      .catch(e => { showError(`Converted, but portal confirm failed: ${e.message}`); load(); });
    load();
  };


  const handlePaymentSaved = (result, reg) => {
    setPay(null);
    showSuccess(`Payment recorded — ${result.receiptNumber}`);
    printColonieReceipt(
      result.registration || reg,
      { receiptNumber: result.receiptNumber, amount: result.amount, balanceRemaining: result.balanceRemaining, paymentMethod: result.paymentMethod, referenceNumber: result.referenceNumber, notes: null },
      result.enrollment || result.registration,
    );
    // Auto-confirm on their portal — skip for Trial (confirm only on conversion)
    const isTrial = (result.registration?.PackageType || reg.PackageType) === 'Trial';
    if (!isTrial && (result.registration?.ExternalStatus || reg.ExternalStatus) !== 'Confirmed') {
      const regId = reg.RegistrationID;
      apiCall(`/colonie/confirm-external/${regId}`, { method: 'POST' })
        .then(r => {
          if (r.success) showSuccess(`✓ ${reg.ChildName} confirmed on Colonie portal`);
          else showError(`Payment recorded, but portal confirm failed: ${r.message}`);
          load(); // reload after confirm so ExternalStatus badge updates
        })
        .catch(e => {
          showError(`Payment recorded, but portal confirm failed: ${e.message}`);
          load();
        });
    }
    load(); // immediate reload for payment status
  };

  const stats = {
    total: regs.length,
    new_: regs.filter(r => r.InternalStatus === 'New').length,
    partial: regs.filter(r => r.InternalStatus === 'PartialPaid').length,
    paid: regs.filter(r => r.InternalStatus === 'Paid').length,
    confirmed: regs.filter(r => r.InternalStatus === 'Confirmed').length,
    collected: regs.reduce((s, r) => s + parseFloat(r.TotalPaid || 0), 0),
    outstanding: regs.reduce((s, r) => s + parseFloat(r.BalanceRemaining || 0), 0),
  };

  const isAdmin = hasRole('GlobalAdmin') || hasRole('Accounting');

  return (
    <div className="space-y-5">

      {/* Banner */}
      <div className="bg-gradient-to-r from-orange-500 via-orange-400 to-yellow-400 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <div className="bg-white/20 p-3 rounded-xl"><Sun size={28}/></div>
            <div>
              <h1 className="text-2xl font-bold">Colonie Des Rêves</h1>
              <p className="text-orange-100 text-sm">Summer Camp 2026 × Collège des Apôtres</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isAdmin && <button onClick={() => setSettings(true)} className="bg-white/20 hover:bg-white/30 p-2 rounded-xl" title="Settings"><Settings size={17}/></button>}
            <button onClick={() => setDaily(true)} className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2"><BarChart2 size={15}/>Daily Report</button>
            {isAdmin && <button onClick={() => setAdmin(true)} className="bg-white text-blue-800 hover:bg-blue-50 px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 shadow-sm"><FileText size={15}/>Full Report</button>}
            <button onClick={handleSync} disabled={syncing} className="bg-white text-orange-600 hover:bg-orange-50 px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 shadow-sm">
              <RefreshCw size={15} className={syncing ? 'animate-spin' : ''}/>{syncing ? 'Syncing…' : 'Sync Portal'}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-4 md:grid-cols-7 gap-3 mt-5">
          {[['Total',stats.total,''],['New',stats.new_,'text-yellow-200'],['Partial',stats.partial,'text-orange-200'],
            ['Paid',stats.paid,'text-green-200'],['Confirmed',stats.confirmed,'text-purple-200'],
            ['Collected',`$${stats.collected.toFixed(0)}`,'text-white'],
            ['Outstanding',`$${stats.outstanding.toFixed(0)}`,stats.outstanding>0?'text-red-200':'text-green-200'],
          ].map(([l,v,c]) => (
            <div key={l} className="bg-white/20 rounded-xl p-2.5 text-center">
              <div className={`text-xl font-bold ${c||'text-white'}`}>{v}</div>
              <div className="text-xs text-orange-100">{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-44">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key==='Enter'&&load()}
            placeholder="Search name, phone… (Enter)"
            className="w-full pl-8 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-orange-400"/>
        </div>
        <select value={filter} onChange={e => setFilter(e.target.value)}
          className="border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-orange-400">
          {['All','New','PartialPaid','Paid','Confirmed'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={load} className="px-4 py-2.5 bg-orange-500 text-white rounded-xl text-sm font-semibold hover:bg-orange-600">Refresh</button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><Loader size={28} className="animate-spin text-orange-500"/></div>
        ) : regs.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <Sun size={44} className="mx-auto mb-3 opacity-25"/>
            <p>No registrations. Click <strong>Sync Portal</strong> to import from Colonie Des Rêves.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-orange-50 text-orange-900 text-xs uppercase">
                  {['Child','Parents','Phone','Class','Package','Total','Paid','Balance','Status','Portal','Actions'].map(h => (
                    <th key={h} className="px-3 py-3 text-left font-semibold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {regs.map(reg => {
                  const bal = parseFloat(reg.BalanceRemaining || 0);
                  const hasEnroll = !!reg.EnrollmentID;
                  const canRenew = hasEnroll && reg.PackageType === 'Monthly3Days' && (reg.MonthsCharged || 0) < 2;
                  const canConfirm = ['Paid','Confirmed'].includes(reg.InternalStatus) && reg.ExternalStatus !== 'Confirmed';
                  return (
                    <tr key={reg.RegistrationID} className="hover:bg-orange-50/30 transition-colors">
                      <td className="px-3 py-3">
                        <div className="font-semibold text-gray-800">{reg.ChildName}</div>
                        {reg.FamilyID && <div className="text-xs text-blue-600 flex items-center gap-1 mt-0.5"><Link size={10}/>{reg.FamilyID}</div>}
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-600"><div>{reg.MotherName}</div><div className="text-gray-400">{reg.FatherName}</div></td>
                      <td className="px-3 py-3 text-xs text-gray-600">{reg.MotherPhone}</td>
                      <td className="px-3 py-3"><div className="font-medium text-gray-700">{reg.Class}</div><div className="text-xs text-gray-400">{reg.Branch}</div></td>
                      <td className="px-3 py-3">
                        {hasEnroll ? (<>
                          <span className="text-xs font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">{PKG_SHORT[reg.PackageType]||reg.PackageType}</span>
                          {reg.PackageType==='Monthly3Days' && <div className="text-xs text-gray-400 mt-0.5">{reg.MonthsCharged||1}mo</div>}
                          {parseFloat(reg.Discount||0) > 0 && (
                            <div className="text-xs text-emerald-600 font-medium mt-0.5" title={reg.DiscountNotes}>
                              -${parseFloat(reg.Discount).toFixed(0)} disc.
                            </div>
                          )}
                        </>) : <span className="text-xs text-gray-400 italic">—</span>}
                      </td>
                      <td className="px-3 py-3 font-semibold text-gray-700">{hasEnroll?`$${parseFloat(reg.TotalCharged||0).toFixed(0)}`:'—'}</td>
                      <td className="px-3 py-3 font-semibold text-green-700">{hasEnroll?`$${parseFloat(reg.TotalPaid||0).toFixed(0)}`:'—'}</td>
                      <td className={`px-3 py-3 font-bold ${bal>0?'text-red-600':hasEnroll?'text-green-600':'text-gray-400'}`}>{hasEnroll?`$${bal.toFixed(0)}`:'—'}</td>
                      <td className="px-3 py-3"><Badge status={reg.InternalStatus}/></td>
                      <td className="px-3 py-3"><Badge status={reg.ExternalStatus}/></td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <button onClick={() => setPay(reg)} disabled={hasEnroll&&bal<=0}
                            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold ${hasEnroll&&bal<=0?'bg-gray-100 text-gray-400 cursor-not-allowed':'bg-green-100 text-green-700 hover:bg-green-200'}`}>
                            <DollarSign size={12}/>{hasEnroll?'Pay':'First Pay'}
                          </button>
                          {hasEnroll && reg.PackageType === 'Trial' && (
                            <button onClick={() => setConvert(reg)} title="Convert trial to real package"
                              className="p-1.5 bg-indigo-100 text-indigo-700 hover:bg-indigo-200 rounded-lg">
                              <RefreshCw size={13}/>
                            </button>
                          )}
                          {hasEnroll && reg.PackageType !== 'Trial' && <button onClick={() => setAddon(reg)} title="Add add-on" className="p-1.5 bg-amber-100 text-amber-700 hover:bg-amber-200 rounded-lg"><Plus size={13}/></button>}

                          {canRenew && <button onClick={() => handleRenewMonthly(reg)} disabled={renewing===reg.RegistrationID} className="p-1.5 bg-indigo-100 text-indigo-700 hover:bg-indigo-200 rounded-lg disabled:opacity-50">{renewing===reg.RegistrationID?<Loader size={13} className="animate-spin"/>:<RotateCcw size={13}/>}</button>}
                          <button onClick={() => setLink(reg)} className={`p-1.5 rounded-lg ${reg.FamilyID?'bg-blue-100 text-blue-600 hover:bg-blue-200':'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}><Link size={13}/></button>
                          {canConfirm && <button onClick={() => handleConfirmExternal(reg)} disabled={confirming===reg.RegistrationID} className="p-1.5 bg-purple-100 text-purple-700 hover:bg-purple-200 rounded-lg disabled:opacity-50">{confirming===reg.RegistrationID?<Loader size={13} className="animate-spin"/>:<ExternalLink size={13}/>}</button>}
                          {reg.InternalStatus==='Confirmed' && <span className="p-1.5 text-green-600" title="Confirmed on portal"><Check size={13}/></span>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pricing reference */}
      <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4">
        <p className="text-xs font-semibold text-orange-700 uppercase mb-3">Pricing Reference — Summer 2026</p>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 text-xs">
          {[{l:'Trial',v:`${P.trial}`,s:'Converts later'},
            {l:'Cotisation',v:`${P.cotisation}`,s:'Full season'},{l:'3Days Season',v:`$${P.season3Days}`,s:'Full season'},
            {l:'Monthly',v:`$${P.monthly3Days}/mo`,s:'Max 2mo'},{l:'Transport',v:'TBD',s:'Per child'},
            {l:'Late 3–3:30',v:`$${P.latePickup3to330}`,s:'Add-on'},{l:'Late 3–4:00',v:`$${P.latePickup3to4}`,s:'Add-on'},
            {l:'Nerd (in)',v:`$${P.nerdInCamp}`,s:'Add-on'},{l:'Nerd Standalone',v:`$${P.nerdOnly}`,s:'Outside camp'},
          ].map(i => (
            <div key={i.l} className="bg-white rounded-xl p-2.5 text-center border border-orange-100">
              <div className="font-bold text-orange-700">{i.v}</div>
              <div className="font-medium text-gray-700 mt-0.5">{i.l}</div>
              <div className="text-gray-400">{i.s}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Modals */}
      {payModal    && <ColoniePaymentModal    registration={payModal}   onClose={()=>setPay(null)}     onSave={r=>handlePaymentSaved(r,payModal)}                      apiCall={apiCall} showError={showError}/>}
      {addonModal  && <ColonieAddAddonModal   registration={addonModal} onClose={()=>setAddon(null)}   onSave={()=>{setAddon(null);showSuccess('Add-on added');load();}} apiCall={apiCall} showError={showError}/>}
      {linkModal   && <ColonieLinkFamilyModal  registration={linkModal} onClose={()=>setLink(null)}    onSave={()=>{setLink(null);showSuccess('Family link updated');load();}} apiCall={apiCall} showError={showError}/>}
      {showDaily   && <ColonieDailyReportModal onClose={()=>setDaily(false)} apiCall={apiCall}/>}
      {showAdmin   && <ColonieAdminReport      onClose={()=>setAdmin(false)} apiCall={apiCall} showError={showError} setPay={reg=>{setAdmin(false);setPay(reg);}}/>}
      {showSettings && <ColonieSettingsModal   onClose={()=>setSettings(false)} apiCall={apiCall} showError={showError} showSuccess={showSuccess}/>}
      {convertModal && <ColonieConvertModal    registration={convertModal} onClose={()=>setConvert(null)} onSave={r=>handleConvertSaved(r,convertModal)} apiCall={apiCall} showError={showError}/>}
    </div>
  );
};

export default Colonie;
